import React from 'react';
import { Link } from 'react-router-dom';
import { Star, TrendingUp, Target } from 'lucide-react';
import type { FrameworkROI } from '../../types/outcomes';

interface FrameworkROIChartProps {
  data: FrameworkROI[];
  limit?: number;
}

export const FrameworkROIChart: React.FC<FrameworkROIChartProps> = ({ 
  data, 
  limit = 10 
}) => {
  const topFrameworks = data.slice(0, limit);
  const maxScore = Math.max(...topFrameworks.map(f => f.impactScore), 1);

  const getEffectivenessColor = (effectiveness: number) => {
    if (effectiveness >= 4.5) return 'text-sage-600';
    if (effectiveness >= 3.5) return 'text-primary-600';
    if (effectiveness >= 2.5) return 'text-[var(--mustard)]';
    return 'text-coral-600';
  };

  return (
    <div className="space-y-4">
      {topFrameworks.map((framework, index) => {
        const barWidth = (framework.impactScore / maxScore) * 100;
        
        return (
          <div key={framework.frameworkId} className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <Link
                to={`/elevation/entry/${framework.frameworkId}`}
                className="font-medium text-charcoal-900 hover:text-primary-600 transition-colors flex items-center gap-2 group flex-1"
              >
                <span className="text-charcoal-500">#{index + 1}</span>
                <span className="group-hover:underline">{framework.frameworkName}</span>
              </Link>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1">
                  <Target className="h-3 w-3 text-charcoal-400" />
                  <span className="text-xs text-charcoal-600">
                    {framework.outcomesImpacted} outcomes
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <TrendingUp className="h-3 w-3 text-charcoal-400" />
                  <span className="text-xs text-charcoal-600">
                    {framework.timesApplied} uses
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Star className={`h-3 w-3 ${getEffectivenessColor(framework.averageEffectiveness)}`} />
                  <span className={`text-xs font-semibold ${getEffectivenessColor(framework.averageEffectiveness)}`}>
                    {framework.averageEffectiveness.toFixed(1)}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="relative h-8 bg-[var(--hover-tint)] overflow-hidden">
              <div
                className="absolute left-0 top-0 h-full bg-gradient-to-r from-primary-400 to-sage-400 transition-all duration-500"
                style={{ width: `${barWidth}%` }}
              />
              <div className="absolute inset-0 flex items-center justify-between px-3">
                <span className="text-xs text-charcoal-700 font-medium">
                  Impact Score: {framework.impactScore}
                </span>
              </div>
            </div>
          </div>
        );
      })}
      
      {data.length === 0 && (
        <div className="text-center py-8 text-charcoal-500">
          <p>No framework applications yet.</p>
          <p className="text-sm mt-2">Apply frameworks to outcomes to see ROI!</p>
        </div>
      )}
    </div>
  );
};

